import { CanActivateFn, NavigationStart, Router, UrlTree } from '@angular/router';
import { inject, Injectable } from '@angular/core';
import { filter } from 'rxjs/operators';
import { RegistrationService } from '../../services/registration.service';
import { DataService } from '../../services/data.service';

export const ENTRY_RESOLVED_NAV_STATE = { entryResolved: true } as const;

const ENTRY_RESOLVED_PENDING_KEY = 'gub_entry_resolved_nav';

/** One-shot flag so AppEntryNavigationCoordinator sees fakesplash-resolved navigations. */
export function markEntryNavigationPending(): void {
  try {
    sessionStorage.setItem(ENTRY_RESOLVED_PENDING_KEY, '1');
  } catch {
    /* storage unavailable */
  }
}

function hasEntryNavigationPending(): boolean {
  try {
    return sessionStorage.getItem(ENTRY_RESOLVED_PENDING_KEY) === '1';
  } catch {
    return false;
  }
}

function clearEntryNavigationPending(): void {
  try {
    sessionStorage.removeItem(ENTRY_RESOLVED_PENDING_KEY);
  } catch {
    /* storage unavailable */
  }
}

/** Pass on in-flow navigations to guest, postcode, login, or org picker while logged in. */
export function entryFlowNavigationExtras(): { state: typeof ENTRY_RESOLVED_NAV_STATE } {
  markEntryNavigationPending();
  return { state: ENTRY_RESOLVED_NAV_STATE };
}

export function normalizeEntryPath(url: string): string {
  const path = url.split('?')[0].split('#')[0];
  if (path.length > 1 && path.endsWith('/')) {
    return path.slice(0, -1);
  }
  return path;
}

export function isLoggedInEntryInterceptPath(path: string): boolean {
  const normalized = normalizeEntryPath(path);
  if (normalized === '/login/nhs') {
    return true;
  }
  if (normalized === '/login') {
    return true;
  }
  if (normalized === '/guest') {
    return true;
  }
  if (normalized === '/select-super-organisation' || normalized.startsWith('/select-super-organisation/')) {
    return true;
  }
  if (normalized === '/postcode-entry' || normalized.startsWith('/postcode-entry/')) {
    return true;
  }
  if (normalized === '/get-started' || normalized === '/access-code') {
    return true;
  }
  return false;
}

function navigationHasEntryResolved(router: Router): boolean {
  if (hasEntryNavigationPending()) {
    return true;
  }
  const nav = router.getCurrentNavigation();
  if (nav?.extras?.state?.['entryResolved'] === true) {
    return true;
  }
  if (typeof history !== 'undefined' && history.state?.['entryResolved'] === true) {
    return true;
  }
  return false;
}

/** Logged-out cold start / logout: guest when a device org exists, else the signup choice. */
export function entryUrl(registrationService: RegistrationService): string {
  if (registrationService.hasDeviceOrganisation()) {
    return '/guest';
  }
  return '/get-started';
}

/**
 * Subscribes to NavigationStart at app bootstrap so logged-in users never paint
 * postcode-entry / org picker before redirecting to fakesplash.
 */
@Injectable({ providedIn: 'root' })
export class AppEntryNavigationCoordinator {
  private started = false;

  constructor(
    private readonly router: Router,
    private readonly data: DataService,
    private readonly registrationService: RegistrationService,
  ) {
    this.start();
  }

  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;
    this.router.events
      .pipe(filter((event): event is NavigationStart => event instanceof NavigationStart))
      .subscribe((event) => {
        void this.handleNavigationStart(event);
      });
  }

  private async handleNavigationStart(event: NavigationStart): Promise<void> {
    if (navigationHasEntryResolved(this.router)) {
      return;
    }

    const targetPath = normalizeEntryPath(event.url);
    if (targetPath === '/introslider') {
      const pendingNhsReturn = this.registrationService.peekNhsCallbackUrl();
      if (pendingNhsReturn) {
        await this.router.navigateByUrl(pendingNhsReturn, { replaceUrl: true });
      }
      return;
    }
    if (targetPath === '/fakesplash') {
      return;
    }

    const loggedIn = this.data.isUserLogged || !!this.data.token;
    if (!loggedIn) {
      if (
        !this.registrationService.isIntroSliderSeen()
        && (
          targetPath === '/postcode-entry'
          || targetPath.startsWith('/postcode-entry/')
          || targetPath === '/get-started'
          || targetPath === '/access-code'
        )
      ) {
        await this.router.navigateByUrl('/introslider', { replaceUrl: true });
      }
      return;
    }

    if (!isLoggedInEntryInterceptPath(targetPath)) {
      return;
    }

    const currentPath = normalizeEntryPath(this.router.url);
    if (currentPath === '/fakesplash') {
      return;
    }

    if (targetPath === '/fakesplash') {
      return;
    }

    await this.router.navigateByUrl('/fakesplash', { replaceUrl: true });
  }

  async loggedOutCanActivate(path: string, fromGuestBack: boolean): Promise<boolean | UrlTree> {
    await this.registrationService.ensureDeviceOrganisation();
    if (navigationHasEntryResolved(this.router)) {
      return true;
    }
    if (fromGuestBack && (path.startsWith('/postcode-entry') || path === '/access-code' || path === '/get-started')) {
      return true;
    }

    const loggedOutEntry = await this.registrationService.loggedOutEntryPath();
    const onboardingPath = path.startsWith('/postcode-entry') || path === '/get-started' || path === '/access-code';

    if (path === '/login' || path === '/login/nhs') {
      if (loggedOutEntry === '/postcode-entry' || loggedOutEntry === '/get-started') {
        return this.router.parseUrl(loggedOutEntry);
      }
      return true;
    }

    if (path === '/guest' && (loggedOutEntry === '/postcode-entry' || loggedOutEntry === '/get-started' || loggedOutEntry === '/introslider')) {
      return this.router.parseUrl(loggedOutEntry);
    }

    if (onboardingPath && loggedOutEntry === '/guest') {
      return this.router.parseUrl('/guest');
    }

    if (onboardingPath && loggedOutEntry === '/introslider') {
      return this.router.parseUrl('/introslider');
    }

    if ((path === '/postcode-entry' || path === '/access-code') && loggedOutEntry === '/get-started') {
      const chosen = typeof localStorage !== 'undefined' ? localStorage.getItem('signupEntry') : null;
      if ((path === '/postcode-entry' && chosen === 'postcode') || (path === '/access-code' && chosen === 'access_code')) {
        return true;
      }
      return this.router.parseUrl('/get-started');
    }

    return true;
  }

  loggedInCanActivate(path: string): boolean | UrlTree {
    if (navigationHasEntryResolved(this.router)) {
      clearEntryNavigationPending();
      return true;
    }
    if (path === '/fakesplash') {
      return true;
    }
    if (!isLoggedInEntryInterceptPath(path)) {
      return true;
    }
    return this.router.parseUrl('/fakesplash');
  }
}

/**
 * Keeps guest, postcode-entry, login, and select-super-organisation aligned for entry flows.
 */
export const appEntryGuard: CanActivateFn = async (_route, state) => {
  const coordinator = inject(AppEntryNavigationCoordinator);
  const data = inject(DataService);

  const path = normalizeEntryPath(state.url);
  const fromGuestBack = inject(Router).getCurrentNavigation()?.extras?.state?.['fromGuestBack'] === true;

  if (data.isUserLogged || data.token) {
    return coordinator.loggedInCanActivate(path);
  }

  return coordinator.loggedOutCanActivate(path, fromGuestBack);
};
